import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  Tooltip,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { motion } from 'framer-motion';
import { alpha, useTheme } from '@mui/material/styles';
import { applicationsApi, type ApplicationListItemDto } from '../api/admin';
import { messageFromOrchestratorAxiosError } from '../lib/axiosErrorMessage';
import { LOAN_OPS_APPLY_FILTERS_EVENT } from '../api/loanOpsChat';
import {
  applicationsListSearchHasFilters,
  applicationsListSearchToRestoreWhenEmpty,
  defaultApplicationsListFilters,
  parseApplicationsListFiltersFromSearch,
  rememberApplicationsListSearch,
  writeApplicationsListFiltersToSearch,
  ymdToApiBound,
  type ApplicationsListFilters,
  type ApplicationsListLocationState,
} from '../lib/applicationsListFilters';
import {
  AdminListPageHeader,
  adminDataTableSx,
  dataTableCardSx,
  filtersPopoverPaperSx,
  listPageFadeIn,
  listSectionFadeIn,
  monoLinkSx,
} from '../components/admin/adminListPageUi';
import { ManualReviewQueuePanel } from '../components/loanReview/ManualReviewQueuePanel';
import { AdminDateRangeField } from '../components/admin/AdminDateRangeField';

const PAGE_SIZE = 25;

const STATUS_OPTIONS = ['', 'ACTIVE', 'MANUAL_REVIEW', 'APPROVED', 'FUNDED', 'DECLINED', 'WITHDRAWN'] as const;

/**
 * Fallback vocabularies for the blockage filters.
 *
 * The live options come from the list response (`filterOptions`), because the orchestrator
 * owns these words and a second copy is a second thing to drift. These stand in only before
 * the first response lands, so the panel is never empty.
 */
const BLOCKED_AREA_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Any' },
  { value: 'device', label: 'Device intelligence' },
  { value: 'identity', label: 'Identity' },
  { value: 'income', label: 'Income' },
  { value: 'decision', label: 'Decision engine' },
  { value: 'employer', label: 'Employer' },
  { value: 'loan_review', label: 'Loan review' },
  { value: 'withdrawn', label: 'Withdrawn' },
  { value: 'other', label: 'Other' },
];

const PRODUCT_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Any' },
  { value: 'INSTALLMENT_LOAN', label: 'Installment loan' },
  { value: 'PAYDAY_LOAN', label: 'Payday loan' },
];

function productTypeLabel(value: string): string {
  return PRODUCT_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value.replace(/_/g, ' ');
}

const BLOCKED_STATE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Any' },
  { value: 'failed', label: 'Failed' },
  { value: 'pending', label: 'Waiting' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const ANY_OPTION = { value: '', label: 'Any' };

function optionLabel(
  options: Array<{ value: string; label: string }>,
  value: string,
): string {
  return options.find((o) => o.value === value)?.label ?? value;
}

function statusLabel(value: string): string {
  if (!value) return 'Any';
  return value.charAt(0) + value.slice(1).toLowerCase();
}

function summaryChipColor(label: string): 'default' | 'success' | 'warning' | 'error' | 'info' {
  switch (label) {
    case 'OK':
    case 'Loan funded':
    case 'Agreement signed':
    case 'Lending decision recorded':
      return 'success';
    case 'Failed':
    case 'Doctrine error':
      return 'error';
    case 'Review':
    case 'In progress':
    case 'Employment under review':
      return 'warning';
    case 'No doctrine runs':
    case 'Not started':
      return 'info';
    default:
      // Milestone progress labels from orchestrator (e.g. "Income verified")
      return 'info';
  }
}

function loanStatusChipColor(status: string): 'default' | 'success' | 'warning' | 'error' | 'info' {
  const u = status.trim().toUpperCase();
  if (u === 'MANUAL_REVIEW') return 'warning';
  if (u === 'ACTIVE' || u === 'APPROVED' || u === 'FUNDED') return 'success';
  if (u === 'DECLINED' || u === 'WITHDRAWN') return 'error';
  return 'default';
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function ApplicationsPage() {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filterBtnRef = useRef<HTMLButtonElement | null>(null);

  const parsed = useMemo(
    () => parseApplicationsListFiltersFromSearch(searchParams),
    [searchParams],
  );
  const applied = parsed.filters;
  const page = parsed.page;

  // Persist filters into the URL when landing on bare `/applications` (navbar, redirects, etc.).
  // Prefer last remembered query so leaving the page does not reset to Opened=today defaults.
  useEffect(() => {
    if (applicationsListSearchHasFilters(searchParams)) return;
    const remembered = applicationsListSearchToRestoreWhenEmpty();
    if (remembered) {
      setSearchParams(new URLSearchParams(remembered), { replace: true });
      return;
    }
    setSearchParams(
      writeApplicationsListFiltersToSearch(
        searchParams,
        defaultApplicationsListFilters(),
        0,
      ),
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  // Remember list query for Back / navbar / any bare `/applications` return.
  useEffect(() => {
    if (!applicationsListSearchHasFilters(searchParams)) return;
    rememberApplicationsListSearch(searchParams);
  }, [searchParams]);

  const commitFilters = useCallback(
    (next: ApplicationsListFilters, nextPage = 0, opts?: { replace?: boolean }) => {
      setSearchParams(writeApplicationsListFiltersToSearch(searchParams, next, nextPage), {
        replace: opts?.replace ?? false,
      });
    },
    [searchParams, setSearchParams],
  );

  const patchApplied = useCallback(
    (patch: Partial<ApplicationsListFilters>) => {
      commitFilters({ ...applied, ...patch }, 0);
    },
    [applied, commitFilters],
  );

  // Draft fields — seeded from URL whenever applied filters change.
  const [applicationIdContains, setApplicationIdContains] = useState(applied.applicationIdContains);
  const [ccidContains, setCcidContains] = useState(applied.ccidContains);
  const [emailContains, setEmailContains] = useState(applied.emailContains);
  const [phoneContains, setPhoneContains] = useState(applied.phoneContains);
  const [status, setStatus] = useState(applied.status);
  const [productType, setProductType] = useState(applied.productType);
  const [blockedArea, setBlockedArea] = useState(applied.blockedArea);
  const [blockedState, setBlockedState] = useState(applied.blockedState);
  const [journeyStage, setJourneyStage] = useState(applied.journeyStage);
  const [editedOnly, setEditedOnly] = useState(applied.editedOnly);
  const [createdFrom, setCreatedFrom] = useState(applied.createdFrom);
  const [createdTo, setCreatedTo] = useState(applied.createdTo);
  const [updatedFrom, setUpdatedFrom] = useState(applied.updatedFrom);
  const [updatedTo, setUpdatedTo] = useState(applied.updatedTo);

  useEffect(() => {
    setApplicationIdContains(applied.applicationIdContains);
    setCcidContains(applied.ccidContains);
    setEmailContains(applied.emailContains);
    setPhoneContains(applied.phoneContains);
    setStatus(applied.status);
    setProductType(applied.productType);
    setBlockedArea(applied.blockedArea);
    setBlockedState(applied.blockedState);
    setJourneyStage(applied.journeyStage);
    setCreatedFrom(applied.createdFrom);
    setCreatedTo(applied.createdTo);
    setUpdatedFrom(applied.updatedFrom);
    setUpdatedTo(applied.updatedTo);
  }, [applied]);

  const queryParams = useMemo(
    () => ({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      applicationIdContains: applied.applicationIdContains || undefined,
      ccidContains: applied.ccidContains || undefined,
      emailContains: applied.emailContains || undefined,
      phoneContains: applied.phoneContains || undefined,
      status: applied.status || undefined,
      productType: applied.productType || undefined,
      blockedArea: applied.blockedArea || undefined,
      blockedState: applied.blockedState || undefined,
      journeyStage: applied.journeyStage || undefined,
      editedOnly: applied.editedOnly === 'true' ? true : undefined,
      createdFrom: applied.createdFrom ? ymdToApiBound(applied.createdFrom, 'start') : undefined,
      createdTo: applied.createdTo ? ymdToApiBound(applied.createdTo, 'end') : undefined,
      updatedFrom: applied.updatedFrom ? ymdToApiBound(applied.updatedFrom, 'start') : undefined,
      updatedTo: applied.updatedTo ? ymdToApiBound(applied.updatedTo, 'end') : undefined,
    }),
    [applied, page],
  );

  const q = useQuery({
    queryKey: ['applications', queryParams],
    queryFn: () => applicationsApi.list(queryParams),
  });

  /*
   * Filter vocabularies come from the response, not from a copy kept here.
   *
   * The milestone labels had already drifted: this file called
   * EMPLOYMENT_PENDING_REVIEW "Employment under review (customer advanced)" while the JOURNEY
   * column — rendered from the orchestrator's label — called it "Employment under review". An
   * option whose text differs from the cell it filters on is the same class of bug as an
   * option that filters on a different field.
   *
   * `keepPreviousData` is not in play here, so the options are held across refetches to stop
   * the selects flickering back to their fallbacks mid-request.
   */
  const served = q.data?.filterOptions;
  const lastServed = useRef<typeof served>(undefined);
  if (served) lastServed.current = served;
  const options = served ?? lastServed.current;

  const withAny = (opts: Array<{ value: string; label: string }> | undefined, fallback: Array<{ value: string; label: string }>) =>
    opts && opts.length ? [ANY_OPTION, ...opts] : fallback;

  const journeyStageOptions = useMemo(
    () => withAny(options?.journeyStages, [ANY_OPTION]),
    [options],
  );
  const blockedAreaOptions = useMemo(
    () => withAny(options?.blockedAreas, BLOCKED_AREA_OPTIONS),
    [options],
  );
  const blockedStateOptions = useMemo(
    () => withAny(options?.blockedStates, BLOCKED_STATE_OPTIONS),
    [options],
  );

  const applyFilters = () => {
    commitFilters({
      applicationIdContains: applicationIdContains.trim(),
      ccidContains: ccidContains.trim(),
      emailContains: emailContains.trim(),
      phoneContains: phoneContains.trim(),
      editedOnly,
      status,
      productType,
      blockedArea,
      blockedState,
      journeyStage,
      createdFrom: createdFrom.trim(),
      createdTo: createdTo.trim(),
      updatedFrom: updatedFrom.trim(),
      updatedTo: updatedTo.trim(),
    });
    setFiltersOpen(false);
  };

  const onFilterKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      applyFilters();
    }
  };

  const clearFilters = () => {
    commitFilters(defaultApplicationsListFilters(), 0);
  };

  const setPage = (nextPage: number) => {
    commitFilters(applied, nextPage, { replace: true });
  };

  useEffect(() => {
    const onApply = (ev: Event) => {
      const detail = (ev as CustomEvent<Record<string, unknown>>).detail || {};
      const str = (key: string) => {
        const v = detail[key];
        return typeof v === 'string' && v.trim() ? v.trim() : '';
      };
      const ymd = (raw: string) => raw.slice(0, 10);
      commitFilters({
        applicationIdContains: '',
        ccidContains: '',
        emailContains: '',
        phoneContains: '',
        status: str('status') ? str('status').toUpperCase() : '',
        productType: '',
        editedOnly: str('editedOnly') === 'true' ? 'true' : '',
        // The assistant may send either name; `stuckStep` is the pre-rename key.
        blockedArea: str('blockedArea') || str('blocked_area') || str('stuckStep') || str('stuck_step'),
        blockedState: str('blockedState') || str('blocked_state'),
        journeyStage: str('journeyStage') || str('journey_stage'),
        createdFrom: ymd(str('createdFrom') || str('created_from') || str('dateFrom') || str('date_from')),
        createdTo: ymd(str('createdTo') || str('created_to') || str('dateTo') || str('date_to')),
        updatedFrom: ymd(str('updatedFrom') || str('updated_from')),
        updatedTo: ymd(str('updatedTo') || str('updated_to')),
      });
      /*
       * Deliberately does NOT open the filter panel.
       *
       * It used to, so the reviewer could see what the assistant had applied. But the
       * panel is a modal with a backdrop, so "show me applications opened today" ended
       * with the results dimmed behind a dialog the reviewer then had to dismiss — hiding
       * the exact thing they asked for.
       *
       * What was applied is already visible without it: activeFilterChips renders each
       * filter above the table, and the URL carries every value.
       */
      setFiltersOpen(false);
    };
    window.addEventListener(LOAN_OPS_APPLY_FILTERS_EVENT, onApply as EventListener);
    return () => window.removeEventListener(LOAN_OPS_APPLY_FILTERS_EVENT, onApply as EventListener);
  }, [commitFilters]);

  const orchestratorDisabled =
    import.meta.env.VITE_ORCHESTRATOR_DISABLED === 'true' ||
    import.meta.env.VITE_ORCHESTRATOR_DISABLED === '1';

  const activeFilterChips = useMemo(() => {
    const chips: Array<{
      key: string;
      label: string;
      color?: 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary';
      onDelete: () => void;
    }> = [];
    if (applied.status) {
      chips.push({
        key: 'status',
        label: `Status: ${statusLabel(applied.status)}`,
        color: loanStatusChipColor(applied.status),
        onDelete: () => patchApplied({ status: '' }),
      });
    }
    if (applied.productType) {
      chips.push({
        key: 'productType',
        label: `Loan type: ${productTypeLabel(applied.productType)}`,
        color: 'primary',
        onDelete: () => patchApplied({ productType: '' }),
      });
    }
    /*
     * Three chips where there was one.
     *
     * The single chip said `Journey: Identity` while the JOURNEY column beside it said
     * `Not started` for the same row, because the chip filtered on where the application
     * stopped and the column showed how far it got. Naming them apart is the fix: `Journey`
     * now means the column, and the blockage keeps its own two words.
     */
    if (applied.journeyStage) {
      chips.push({
        key: 'journeyStage',
        label: `Journey: ${optionLabel(journeyStageOptions, applied.journeyStage)}`,
        color: 'info',
        onDelete: () => patchApplied({ journeyStage: '' }),
      });
    }
    if (applied.blockedArea) {
      chips.push({
        key: 'blockedArea',
        label: `Blocked at: ${optionLabel(blockedAreaOptions, applied.blockedArea)}`,
        color: 'warning',
        onDelete: () => patchApplied({ blockedArea: '' }),
      });
    }
    if (applied.blockedState) {
      chips.push({
        key: 'blockedState',
        label: `State: ${optionLabel(blockedStateOptions, applied.blockedState)}`,
        color: applied.blockedState === 'failed' ? 'error' : 'default',
        onDelete: () => patchApplied({ blockedState: '' }),
      });
    }
    if (applied.applicationIdContains) {
      chips.push({
        key: 'applicationId',
        label: `Application ID: ${applied.applicationIdContains}`,
        color: 'primary',
        onDelete: () => patchApplied({ applicationIdContains: '' }),
      });
    }
    if (applied.ccidContains) {
      chips.push({
        key: 'ccid',
        label: `Customer ID: ${applied.ccidContains}`,
        color: 'primary',
        onDelete: () => patchApplied({ ccidContains: '' }),
      });
    }
    if (applied.emailContains) {
      chips.push({
        key: 'email',
        label: `Email: ${applied.emailContains}`,
        color: 'primary',
        onDelete: () => patchApplied({ emailContains: '' }),
      });
    }
    if (applied.phoneContains) {
      chips.push({
        key: 'phone',
        label: `Phone: ${applied.phoneContains}`,
        color: 'primary',
        onDelete: () => patchApplied({ phoneContains: '' }),
      });
    }
    if (applied.createdFrom || applied.createdTo) {
      chips.push({
        key: 'created',
        label: `Opened ${applied.createdFrom || '…'} → ${applied.createdTo || '…'}`,
        color: 'primary',
        onDelete: () => patchApplied({ createdFrom: '', createdTo: '' }),
      });
    }
    if (applied.updatedFrom || applied.updatedTo) {
      chips.push({
        key: 'updated',
        label: `Updated ${applied.updatedFrom || '…'} → ${applied.updatedTo || '…'}`,
        color: 'primary',
        onDelete: () => patchApplied({ updatedFrom: '', updatedTo: '' }),
      });
    }
    return chips;
  }, [applied, patchApplied]);

  const activeFilterCount = activeFilterChips.length;

  return (
    <Box component={motion.div} {...listPageFadeIn}>
      <AdminListPageHeader
        title="Loan applications"
        subtitle="Search by application ID, customer ID, email, or phone. Open a row for the full journey."
      />

      <ManualReviewQueuePanel />

      {orchestratorDisabled && (
        <motion.div {...listSectionFadeIn(0.04)}>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            <Typography variant="body2" component="div" sx={{ mb: 0.75 }}>
              <strong>Orchestrator is not proxied in this dev build</strong> (
              <code>VITE_ORCHESTRATOR_DISABLED</code>). Requests go to the Vite stub at{' '}
              <code>/api/orchestrator/v1/*</code>, not a real orchestrator on port 3000.
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 0.75 }}>
              The applications list below loads from that stub (sample rows by default). For{' '}
              <strong>real</strong> loan applications from your DB: unset{' '}
              <code>VITE_ORCHESTRATOR_DISABLED</code>, run the orchestrator, and keep the default proxy to{' '}
              <code>VITE_ORCHESTRATOR_PROXY_TARGET</code> (or set <code>VITE_ORCHESTRATOR_API_BASE</code> to a running
              API).
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Empty stub list: set <code>VITE_ORCHESTRATOR_STUB_APPLICATIONS=false</code> in{' '}
              <code>apps/admin/.env</code>.
            </Typography>
          </Alert>
        </motion.div>
      )}

      <motion.div {...listSectionFadeIn(0.08)}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ mb: 2 }}
        >
          {activeFilterCount > 0 ? (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mr: 'auto' }}>
              {activeFilterChips.map((chip) => (
                <Chip
                  key={chip.key}
                  size="small"
                  label={chip.label}
                  color={chip.color ?? 'primary'}
                  onDelete={chip.onDelete}
                  sx={{ fontWeight: 600, maxWidth: '100%' }}
                />
              ))}
            </Stack>
          ) : null}
          <Badge
            color="primary"
            badgeContent={activeFilterCount || undefined}
            overlap="circular"
            sx={{ '& .MuiBadge-badge': { fontWeight: 800 } }}
          >
            <Button
              ref={filterBtnRef}
              variant={filtersOpen ? 'contained' : 'outlined'}
              size="medium"
              startIcon={<FilterListRoundedIcon />}
              endIcon={<ExpandMoreRoundedIcon sx={{ transform: filtersOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />}
              onClick={() => setFiltersOpen((o) => !o)}
              aria-expanded={filtersOpen}
              aria-haspopup="dialog"
              sx={{
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 999,
                px: 2,
                bgcolor: filtersOpen ? undefined : 'background.paper',
                borderColor: 'divider',
                boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.06)}`,
              }}
            >
              Filters
            </Button>
          </Badge>
        </Stack>

        <Popover
          open={filtersOpen}
          anchorEl={filterBtnRef.current}
          onClose={(_event, _reason) => {
            const target = (_event as Event | undefined)?.target as HTMLElement | null | undefined;
            // Keep open while using nested Select menus / date-range popovers (portaled).
            if (
              target?.closest?.(
                '[data-admin-date-range-popover], .MuiMenu-root, .MuiModal-root:not(.MuiPopover-root)',
              )
            ) {
              return;
            }
            if (target?.closest?.('.MuiPopover-paper') && !target.closest('[data-admin-filters-popover]')) {
              return;
            }
            setFiltersOpen(false);
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              'data-admin-filters-popover': '',
              sx: filtersPopoverPaperSx(theme),
            } as object,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <FilterListRoundedIcon sx={{ fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Filter applications
              </Typography>
            </Stack>
            <IconButton size="small" aria-label="Close filters" onClick={() => setFiltersOpen(false)}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ px: 2, pt: 1.75, pb: 1.5 }} onKeyDown={onFilterKeyDown}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 1,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              Find by
            </Typography>
            <Stack spacing={1.25}>
              <TextField
                size="small"
                fullWidth
                label="Application ID"
                placeholder="Part of the ID…"
                value={applicationIdContains}
                onChange={(e) => setApplicationIdContains(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                size="small"
                fullWidth
                label="Customer ID"
                placeholder="CCID contains…"
                value={ccidContains}
                onChange={(e) => setCcidContains(e.target.value)}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                <TextField
                  size="small"
                  fullWidth
                  label="Email"
                  placeholder="customer@example.com"
                  value={emailContains}
                  onChange={(e) => setEmailContains(e.target.value)}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Phone"
                  placeholder="+1…"
                  value={phoneContains}
                  onChange={(e) => setPhoneContains(e.target.value)}
                />
              </Stack>
            </Stack>

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 2,
                mb: 1,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              Loan & dates
            </Typography>
            <Stack spacing={1.25}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="app-status-label">Loan status</InputLabel>
                  <Select
                    labelId="app-status-label"
                    label="Loan status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    MenuProps={{ disablePortal: true }}
                  >
                    <MenuItem value="">
                      <em>Any</em>
                    </MenuItem>
                    {STATUS_OPTIONS.filter(Boolean).map((s) => (
                      <MenuItem key={s} value={s}>
                        {statusLabel(s)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel id="app-product-type-label">Loan type</InputLabel>
                  <Select
                    labelId="app-product-type-label"
                    label="Loan type"
                    value={productType}
                    onChange={(e) => setProductType(e.target.value)}
                    MenuProps={{ disablePortal: true }}
                  >
                    {PRODUCT_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value || 'any'} value={opt.value}>
                        {opt.value ? opt.label : <em>{opt.label}</em>}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel id="app-journey-stage-label">Journey</InputLabel>
                  <Select
                    labelId="app-journey-stage-label"
                    label="Journey"
                    value={journeyStage}
                    onChange={(e) => setJourneyStage(e.target.value)}
                    MenuProps={{ disablePortal: true }}
                  >
                    {journeyStageOptions.map((opt) => (
                      <MenuItem key={opt.value || 'any'} value={opt.value}>
                        {opt.value ? opt.label : <em>{opt.label}</em>}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {/* Finding the applications staff have corrected is its own
                    question — "which of these did a human touch, and why". */}
                <FormControl size="small" fullWidth>
                  <InputLabel id="app-edited-only-label">Manual edits</InputLabel>
                  <Select
                    labelId="app-edited-only-label"
                    label="Manual edits"
                    value={editedOnly}
                    onChange={(e) => setEditedOnly(e.target.value)}
                    MenuProps={{ disablePortal: true }}
                  >
                    <MenuItem value="">
                      <em>Any</em>
                    </MenuItem>
                    <MenuItem value="true">Edited by a reviewer</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                <FormControl size="small" fullWidth>
                  <InputLabel id="app-blocked-area-label">Blocked at</InputLabel>
                  <Select
                    labelId="app-blocked-area-label"
                    label="Blocked at"
                    value={blockedArea}
                    onChange={(e) => setBlockedArea(e.target.value)}
                    MenuProps={{ disablePortal: true }}
                  >
                    {blockedAreaOptions.map((opt) => (
                      <MenuItem key={opt.value || 'any'} value={opt.value}>
                        {opt.value ? opt.label : <em>{opt.label}</em>}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel id="app-blocked-state-label">State</InputLabel>
                  <Select
                    labelId="app-blocked-state-label"
                    label="State"
                    value={blockedState}
                    onChange={(e) => setBlockedState(e.target.value)}
                    MenuProps={{ disablePortal: true }}
                  >
                    {blockedStateOptions.map((opt) => (
                      <MenuItem key={opt.value || 'any'} value={opt.value}>
                        {opt.value ? opt.label : <em>{opt.label}</em>}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <AdminDateRangeField
                label="Opened"
                value={{ from: createdFrom, to: createdTo }}
                onChange={({ from, to }) => {
                  setCreatedFrom(from);
                  setCreatedTo(to);
                }}
              />
              <AdminDateRangeField
                label="Updated"
                value={{ from: updatedFrom, to: updatedTo }}
                onChange={({ from, to }) => {
                  setUpdatedFrom(from);
                  setUpdatedTo(to);
                }}
              />
            </Stack>
          </Box>

          <Box sx={{ px: 2, py: 1.25 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SearchRoundedIcon />}
                  onClick={applyFilters}
                  sx={{ fontWeight: 800, textTransform: 'none', px: 2, borderRadius: 999 }}
                >
                  Apply
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    clearFilters();
                    setFiltersOpen(false);
                  }}
                  sx={{ fontWeight: 700, textTransform: 'none', color: 'text.secondary', borderRadius: 999 }}
                >
                  Reset
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Enter to apply
              </Typography>
            </Stack>
          </Box>
        </Popover>
      </motion.div>

      {q.isPending && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={36} />
        </Box>
      )}

      {q.isError && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => q.refetch()} sx={{ fontWeight: 700 }}>
              Retry
            </Button>
          }
        >
          {messageFromOrchestratorAxiosError(q.error) || 'Failed to load applications.'}
        </Alert>
      )}

      {q.data && (
        <motion.div {...listSectionFadeIn(0.14)}>
          <Card variant="outlined" sx={dataTableCardSx(theme)}>
            <Table size="small" stickyHeader sx={adminDataTableSx(theme)}>
              <TableHead>
                <TableRow>
                  <TableCell>Application</TableCell>
                  <TableCell>CCID</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Journey</TableCell>
                  <TableCell>Doctrines</TableCell>
                  <TableCell>Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {q.data.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                        <Typography variant="body2" color="text.secondary">
                          No loan applications match the current filters.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  q.data.items.map((row: ApplicationListItemDto, index: number) => (
                    <TableRow
                      key={row.applicationId}
                      hover
                      component={motion.tr}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.22, delay: Math.min(index * 0.03, 0.36) }}
                    >
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Box
                          component={RouterLink}
                          to={`/applications/${encodeURIComponent(row.applicationId)}`}
                          state={
                            {
                              applicationsListSearch: searchParams.toString(),
                            } satisfies ApplicationsListLocationState
                          }
                          sx={monoLinkSx(theme)}
                        >
                          {row.applicationId}
                        </Box>
                        {/* Row-level edit marker. A corrected application must not
                            read as untouched at any zoom level — field, header, list. */}
                        {row.manualEditCount ? (
                          <Tooltip
                            title={`${row.manualEditCount} manual change${
                              row.manualEditCount === 1 ? '' : 's'
                            } recorded by a reviewer`}
                            arrow
                          >
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={`edited · ${row.manualEditCount}`}
                              sx={{
                                ml: 0.75,
                                height: 17,
                                fontSize: '0.62rem',
                                fontWeight: 700,
                                verticalAlign: 'middle',
                              }}
                            />
                          </Tooltip>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.ccid ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.email ?? '—'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 140, whiteSpace: 'nowrap' }}>{row.phone ?? '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.status}
                          color={loanStatusChipColor(row.status)}
                          sx={{ fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={summaryChipColor(row.journeySummaryLabel)}
                          label={row.journeySummaryLabel}
                          sx={{ fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" gap={0.5} flexWrap="wrap" useFlexGap>
                          {row.doctrineRollups.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          ) : (
                            row.doctrineRollups.map((d) => (
                              <Chip
                                key={d.doctrineId}
                                size="small"
                                label={`${d.doctrineId.replace(/_VERIFICATION$/, '')}: ${d.outcome ?? d.status}`}
                                variant="outlined"
                                sx={{ fontWeight: 600 }}
                              />
                            ))
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatDateTime(row.updatedAt)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={q.data.total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              sx={{
                borderTop: 1,
                borderColor: 'divider',
                '.MuiTablePagination-toolbar': { minHeight: 52 },
              }}
            />
          </Card>
        </motion.div>
      )}
    </Box>
  );
}
